export type SttLanguage = "tr" | "en" | "auto";

/** Gateway route that proxies the voice service's `WS /stt` (session-authenticated). */
export const STT_PATH = "/api/v1/voice/stt";

/** Server events, forwarded verbatim from the voice service. */
export type SttEvent =
  | { type: "ready" }
  | { type: "speech_start"; ms: number }
  | { type: "transcript"; text: string; final: boolean; language: string }
  | { type: "error"; message: string }
  | { type: "closed" };

/** The subset of the WHATWG `WebSocket` the client uses. */
export interface SttSocket {
  binaryType: string;
  readonly readyState: number;
  send(data: string | ArrayBufferLike | ArrayBufferView): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}
export type SttSocketCtor = new (url: string) => SttSocket;

export class SttError extends Error {
  override readonly name = "SttError";
}

/** `ws(s)://<host>/api/v1/voice/stt?language=...` on the page's own origin. */
export function sttUrl(
  language: SttLanguage,
  loc: Pick<Location, "protocol" | "host"> = globalThis.location,
): string {
  const proto = loc.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${loc.host}${STT_PATH}?language=${encodeURIComponent(language)}`;
}

export function parseSttEvent(raw: unknown): SttEvent | null {
  if (typeof raw !== "string") return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v !== "object") return null;
  const e = v as Record<string, unknown>;
  switch (e["type"]) {
    case "ready":
    case "closed":
      return { type: e["type"] };
    case "speech_start":
      return { type: "speech_start", ms: typeof e["ms"] === "number" ? e["ms"] : 0 };
    case "transcript":
      if (typeof e["text"] !== "string") return null;
      return {
        type: "transcript",
        text: e["text"],
        final: e["final"] !== false,
        language: typeof e["language"] === "string" ? e["language"] : "",
      };
    case "error":
      return { type: "error", message: typeof e["message"] === "string" ? e["message"] : "error" };
    default:
      return null;
  }
}

export interface SttSessionOptions {
  url: string;
  /** Session token, or null to let the gateway use the session cookie. */
  token: string | null;
  WebSocket?: SttSocketCtor;
  onEvent?: (event: SttEvent) => void;
}

export interface SttSession {
  /** Queues one 16 kHz mono PCM frame (sent once the socket is open and authenticated). */
  sendPcm(frame: Int16Array): void;
  /** Ends the input; resolves with the joined final transcripts. */
  end(): Promise<string>;
  /** Drops the session without waiting for a transcript. */
  abort(): void;
}

const OPEN = 1;

/**
 * One push-to-talk utterance over the gateway's STT WebSocket: first text frame `auth`, then
 * binary `pcm_s16le` frames, then `{"type":"end"}`. Transcripts arrive per utterance; the
 * session resolves after the server's `closed` event (or the socket closing after `end`).
 */
export function openSttSession(opts: SttSessionOptions): SttSession {
  const Ctor = opts.WebSocket ?? (globalThis.WebSocket as unknown as SttSocketCtor);
  const socket = new Ctor(opts.url);
  socket.binaryType = "arraybuffer";
  const queue: (Int16Array | string)[] = [];
  const texts: string[] = [];
  let opened = false;
  let ended = false;
  let settled = false;
  let resolveEnd!: (text: string) => void;
  let rejectEnd!: (err: Error) => void;
  const result = new Promise<string>((res, rej) => {
    resolveEnd = res;
    rejectEnd = rej;
  });
  // Avoid an unhandled rejection when the caller aborts before calling end().
  result.catch(() => undefined);

  const finish = (err?: Error) => {
    if (settled) return;
    settled = true;
    if (err) rejectEnd(err);
    else resolveEnd(texts.join(" ").trim());
    if (socket.readyState === OPEN) socket.close(1000);
  };
  const flush = () => {
    if (!opened || socket.readyState !== OPEN) return;
    while (queue.length) socket.send(queue.shift()!);
  };

  socket.onopen = () => {
    opened = true;
    socket.send(JSON.stringify({ type: "auth", token: opts.token ?? "cookie" }));
    flush();
  };
  socket.onmessage = (ev) => {
    const event = parseSttEvent(ev.data);
    if (!event) return;
    opts.onEvent?.(event);
    if (event.type === "transcript" && event.final && event.text.trim()) {
      texts.push(event.text.trim());
    } else if (event.type === "error") {
      finish(new SttError(event.message));
    } else if (event.type === "closed") {
      finish();
    }
  };
  socket.onerror = () => finish(new SttError("speech service connection failed"));
  socket.onclose = (ev) => {
    if (ended && (ev.code === 1000 || ev.code === 1005)) finish();
    else finish(new SttError(`speech service closed (${ev.code})`));
  };

  return {
    sendPcm(frame) {
      if (ended || settled) return;
      queue.push(frame);
      flush();
    },
    end() {
      if (!ended) {
        ended = true;
        queue.push(JSON.stringify({ type: "end" }));
        flush();
      }
      return result;
    },
    abort() {
      ended = true;
      finish(new SttError("aborted"));
      try {
        socket.close(1000);
      } catch {
        // Already closed.
      }
    },
  };
}
